import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages ,useIntl } from 'react-intl';
import { Autocomplete, Checkbox, TextField, Option,Button, Typography, TextArea, Tabs, Tab, TabPanel } from '@getgo/chameleon-web-react-wrapper';
import { AddOutlinedIcon } from '@getgo/chameleon-icons/react';
import classes from '../../roles/components/role-details';
import mt from 'core/message-translations';
import st from 'core/shared-translations';

const t = defineMessages({

  'scope-management.input-field-id.helper-text': {
    id: 'scope-management.input-field-id.helper-text',
    defaultMessage: 'Give the scope a unique name to make it easier to identify.',
    description: 'Helper text for the ID input field'
  },
  'scope-management.input-field-groupid.helper-text': {
    id: 'scope-management.input-field-groupid.helper-text',
    defaultMessage: 'Valid values: collab, support, org, admin, profile, g2c.',
    description: 'Helper text for the Group Id input field'
  },
  'scope-management.input-field-public.helper-text': {
    id: 'scope-management.input-field-public.helper-text',
    defaultMessage: 'When set to true scope will be displayed on UI. Default value will be false. When this field is set true, it will be displayed to third party developers in CMP.',
    description: 'Helper text for the Public checkbox'
  },
  'scope.management.input-field-owner.helper-text': {
    id: 'scope.management.input-field-owner.helper-text',
    defaultMessage: 'Select who you want to approve this scope.',
    description: 'Helper text for the owner input field'
  },
  'scope.managment.description.helpertext': {
    id: 'scope.managment.description.helpertext',
    defaultMessage: 'Add description for each supported language.',
    description: 'Helper text for the description input'
  },
});

const allLocales = ["EN", "DE", "ES", "FR", "IT", "JA", "KO", "PT", "ZH"];

const ScopeDetails = ({
    isEditMode,
    scope,
    onChange,
    onBlur,
    errors,
    autocompleteList,
    ownersList,
    autoCompleteRef,
    onClickAddOwner
}) => {
    const intl = useIntl();
  //newautocompletelist contains ownerslist which is needed to be showed in Autocomplete when owners is already present
  // and also it contains role.approvers because so the it can accomodate new owners added which is not present in autocomplete list
  const newautocompleteList = [...new Set([...(autocompleteList), ...(ownersList || []), ...(scope.approvers || [])])];
  return (
    <Box className={classes['roleDetailsContainer']}>
      <TextField
        id="id"
        name="name"
        role="textbox"
        value={scope.id}
        title={intl.formatMessage(st["shared.role-management.table.column1"])}
        onChange={onChange}
        onBlur={onBlur}
        error={
          (errors.id && (intl.formatMessage(st[errors.id] || mt[errors.id])))
        }
        disabled={isEditMode}
        fullwidth
        maxLength={50}
        helperText={
          (!isEditMode && (errors.id &&
              intl.formatMessage(st[errors.id] || mt[errors.id])) )
        }
      >
        <FormattedMessage {...st["shared.role-management.input-field-id.title"]} />
      </TextField>
      <TextField
        id="groupId"
        name="groupId"
        role="textbox"
        value={scope.groupId}
        title={intl.formatMessage(st["shared.scope-management.table.column2"])}
        onChange={onChange}
        onBlur={onBlur}
        error={
          (errors.groupId && (intl.formatMessage(t[errors.groupId])))
        }
        disabled={isEditMode}
        fullwidth
        maxLength={30}
        helperText={
          (!isEditMode && 
          intl.formatMessage(t["scope-management.input-field-groupid.helper-text"]))
        }
      >
        <FormattedMessage {...st["shared.scope-management.table.column2"]} />
      </TextField>
      <Autocomplete
        multiple
        onInput={onChange}
        onChange={onChange}
        id="approvers"
        ref={autoCompleteRef}
        label={intl.formatMessage(st['shared.role.management.input-field-owner.title'])}
        title={intl.formatMessage(st['shared.role.management.input-field-owner.title'])}
        helperText={(errors.approvers &&
          (intl.formatMessage(st[errors.approvers] || mt[errors.approvers]))) ||
          intl.formatMessage(t['scope.management.input-field-owner.helper-text'])}
        fullWidth
        error={(errors.approvers && (intl.formatMessage(st[errors.approvers] || mt[errors.approvers])))}
        listboxStart={
          <Button
            variant="tertiary"
            leadingIcon={<AddOutlinedIcon />}
            onClick={onClickAddOwner}>
            <FormattedMessage {...st['shared.role.management.autocomplete.add.newowner']} />
          </Button>
        }>
        {newautocompleteList && newautocompleteList.map((item) => (
          <Option
            id={item}
            key={item}
            label={item}
            selected={ownersList?.includes(item) || scope.approvers?.includes(item)}>
            <Typography>{item}</Typography>
          </Option>
        ))}
      </Autocomplete>
      <Checkbox
        id="public"
        label={intl.formatMessage(st['shared.scope.managment.public'])}
        onChange={onChange}
        checked={scope.public}
      >
        <Typography data-testid="role-public-checkbox">
          <FormattedMessage {...st['shared.scope.managment.public']} />
        </Typography>
      </Checkbox>
      <Typography variant="caption-medium" color="type-color-secondary">
        {intl.formatMessage(t['scope-management.input-field-public.helper-text'])}
      </Typography>
      <div style={{ margin: '8px 0px' }}>
        <Checkbox
          id="active"
          label={intl.formatMessage(st['shared.role.managment.active'])}
          onChange={onChange}
          checked={scope.active}
        >
          <Typography data-testid="role-active-checkbox">
            <FormattedMessage {...st['shared.role.managment.active']} />
          </Typography>
        </Checkbox>
      </div>
      <div>
        <Typography variant="caption-medium-strong">
          <FormattedMessage {...st['shared.role.managment.description']} />
        </Typography>
        <Typography variant="caption-medium" color="type-color-secondary">
          <FormattedMessage {...t['scope.managment.description.helpertext']} />
        </Typography>
      </div>
      <Tabs fullwidth noPadding>
        {!!allLocales.length && allLocales.map(locale => 
          <React.Fragment key={locale}>
            <Tab>{locale}</Tab>
            <TabPanel>
              <TextArea
                id={`description_${locale}`}
                name={`description_${locale}`}
                helpText=""
                resize="vertical"
                onChange={onChange}
                value={scope.description ? scope.description[locale.toLowerCase()] : ''}
                onBlur={onBlur}
                cols="40"
                rows="5"
                maxLength={500}
                cssClass="roleDescription"
                error={errors.description}
                fullwidth
              />
            </TabPanel>
          </React.Fragment>
        )}
      </Tabs>    
    </Box>
    );
};

ScopeDetails.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  scope: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  autocompleteList: PropTypes.array,
  ownersList: PropTypes.array.isRequired,
  autoCompleteRef: PropTypes.object.isRequired,
  onClickAddOwner: PropTypes.func.isRequired,
};

export default ScopeDetails;
